import { FormattedMessage, IntlProvider } from 'react-intl';
import { NavLink } from 'react-router';
import { Suspense, lazy, useEffect, useState } from 'react';

import { DefaultUserMenu, EulaConsent } from '@rio-cloud/rio-user-menu-component';
import { SessionExpiredDialog } from '@rio-cloud/rio-session-expired-info';
import ApplicationHeader from '@rio-cloud/rio-uikit/ApplicationHeader';
import NotificationsContainer from '@rio-cloud/rio-uikit/NotificationsContainer';
import ApplicationLayout from '@rio-cloud/rio-uikit/ApplicationLayout';
import IframeResizer from '@rio-cloud/iframe-resizer';

import { config } from '../../config';
import { Tags } from './tags/Tags';
import { InfoButton } from './tags/groups-info/InfoButton';
import { RioNotifications } from '@rio-cloud/rio-notifications-component';
import { DEFAULT_LOCALE } from '../../configuration/lang/lang';
import { useAppDispatch, useAppSelector } from '../../configuration/setup/hooks';
import { getIdToken } from '../../configuration/tokenHandling/tokenHandlingSlice';
import { getLanguageData, getLocale } from '../../configuration/lang/langSlice';
import { isUserSessionExpired } from '../../configuration/login/loginSlice';
import { getSessionExpiredAcknowledged, hideSessionExpiredDialog } from './appSlice';
import {
    hasAnalyticsAnalysisOverlayParam,
    removeAnalyticsAnalysisOverlayParam,
} from './utils/analyticsAnalysisOverlayUtils';

const AnalyticsAnalysisOverlay = lazy(() =>
    import('@rio-cloud/rio-uikit/AnalyticsAnalysisOverlay').then(module => ({
        default: module.AnalyticsAnalysisOverlay,
    }))
);

const App = () => {
    const dispatch = useAppDispatch();
    const [showAnalyticsAnalysisOverlay] = useState(() => {
        if (typeof window === 'undefined') {
            return false;
        }

        return hasAnalyticsAnalysisOverlayParam();
    });

    const homeRoute = config.homeRoute as string;
    const isLocalhost = import.meta.env.DEV;
    const getLocalUrl = (port: number) => `http://${window.location.hostname}:${port}`;
    const resolveLink = (liveUrl: string | undefined, localPort: number) =>
        isLocalhost ? getLocalUrl(localPort) : (liveUrl ?? '');
    const languageData = useAppSelector(getLanguageData);
    const showSessionExpired = useAppSelector(
        state => isUserSessionExpired(state) && !getSessionExpiredAcknowledged(state)
    );
    const userLocale = useAppSelector(getLocale);

    const navItems = [
        {
            key: 'assets',
            route: (
                <a href={resolveLink(config.links.assetAdministration, 3010)}>
                    <FormattedMessage id={'tags.tabTitle.assets'} />
                </a>
            ),
        },
        {
            key: 'drivers',
            route: (
                <a href={resolveLink(config.links.driverAdministration, 3011)}>
                    <FormattedMessage id={'tags.tabTitle.drivers'} />
                </a>
            ),
        },
        {
            key: 'tags',
            route: (
                <NavLink to={'/tags'}>
                    <FormattedMessage id={'tags.tabTitle'} />
                </NavLink>
            ),
        },
    ];

    const appTitle = <FormattedMessage id={'tags.moduleName'} />;
    const environment = import.meta.env.PROD ? 'production' : 'local';
    // biome-ignore lint/style/noNonNullAssertion: suppress out of convenience during migration to biome
    const idToken = useAppSelector(getIdToken)!;
    const postLogout = config.postLogoutRedirectUri
        ? {
              redirectUri: config.postLogoutRedirectUri,
              idTokenHint: idToken,
          }
        : undefined;
    const userMenu = <DefaultUserMenu environment={environment} postLogout={postLogout} />;
    const notifications = <RioNotifications />;
    const infoButton = <InfoButton />;
    const menuUrl = config.backend.MENU_SERVICE as string;
    const appNavigator = <IframeResizer className='iFrameResizer' src={menuUrl} />;

    // biome-ignore lint/a11y/useAnchorContent: suppress out of convenience during migration to biome
    const homeLink = <a href={isLocalhost ? window.location.origin : homeRoute} />;

    useEffect(() => {
        if (!showAnalyticsAnalysisOverlay) {
            return;
        }

        removeAnalyticsAnalysisOverlayParam();
    }, [showAnalyticsAnalysisOverlay]);

    return (
        <IntlProvider defaultLocale={DEFAULT_LOCALE} key={userLocale} locale={userLocale} messages={languageData}>
            <ApplicationLayout className={'StarterTemplate'}>
                <ApplicationLayout.Header>
                    <ApplicationHeader
                        label={appTitle}
                        appNavigator={appNavigator}
                        homeRoute={homeLink}
                        navItems={navItems}
                        actionBarItems={[infoButton, notifications, userMenu]}
                    />
                    <EulaConsent environment={environment} />
                </ApplicationLayout.Header>
                <NotificationsContainer />
                <SessionExpiredDialog
                    locale={userLocale}
                    onClose={() => dispatch(hideSessionExpiredDialog())}
                    show={showSessionExpired}
                />
                <Tags />
                {showAnalyticsAnalysisOverlay && (
                    <Suspense fallback={null}>
                        <AnalyticsAnalysisOverlay
                            bannerStartCoordinates={{ top: Math.max(0, window.innerHeight - 85), left: 15 }}
                        />
                    </Suspense>
                )}
            </ApplicationLayout>
        </IntlProvider>
    );
};

export default App;
